// ✅ THEME-ONLY UPDATE (match your StudentAttendanceReport / orange header + blue surfaces)
// - Adds missing shared sx: pageWrapSx/rootPaperSx/headerSx/bodySx/surfaceSx/sectionSx
// - Inputs/Select: focus label ORANGE + outline borders (same as report theme)
// - Table head: BLUE (rgba(25,118,210,0.95)) like other pages (not orange)
// - Zebra + hover rows consistent
// - Toast/Alert styling consistent
// - Keeps ALL business logic + chart logic same

import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Snackbar,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "./config";

// Register chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AnalyticsAttendance = () => {
  const navigate = useNavigate();

  // ✅ Theme constants (match report page)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  // ✅ INPUTS: outline + focus label orange
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  // ✅ Select: same border behavior as TextField
  const selectSx = {
    ...inputSx,
    "& .MuiSelect-select": { py: 1.05 },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
      textAlign: "center",
    },
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.75 },
  });

  const token = localStorage.getItem("auth");

  const [filters, setFilters] = useState({
    class: "",
    teacher: "",
    date: new Date().toISOString().split("T")[0],
  });

  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [attendanceData, setAttendanceData] = useState({
    labels: [],
    datasets: [],
  });

  const [analyticsData, setAnalyticsData] = useState([]);
  const [error, setError] = useState("");

  const [logincampusCode, setCampusCode] = useState("");
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
    } catch (err) {
      console.error("Error decoding token:", err);
      setCampusCode("");
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  // Fetch classes and teachers
  useEffect(() => {
    if (!logincampusCode) return;

    let cancelled = false;
    const fetchData = async () => {
      setError("");
      setLoadingLookups(true);
      try {
        const classResponse = await axios.get(`${API_URL}/api/AnalyticsDataClasses`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });
        if (!cancelled) setClasses(classResponse.data || []);

        const teacherResponse = await axios.get(`${API_URL}/api/AnalyticsDataTeachers`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });
        if (!cancelled) setTeachers(teacherResponse.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        if (!cancelled) setError("Failed to fetch class or teacher data.");
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [logincampusCode, authHeader]);

  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
      : "";

  // Fetch attendance + missing analytics
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!logincampusCode) return;

      setError("");
      setLoadingAnalytics(true);

      try {
        const filtereddate = filters.date || new Date().toISOString().split("T")[0];

        const attendanceResponse = await axios.get(`${API_URL}/api/AnalyticsAttendance`, {
          ...authHeader,
          params: {
            classID: filters.class || undefined,
            teacherName: filters.teacher || undefined,
            attendanceDate: filtereddate,
            CampusCode: logincampusCode,
          },
        });

        const missingAttendanceResponse = await axios.get(`${API_URL}/api/AnalyticsMissingAttendance`, {
          ...authHeader,
          params: { attendanceDate: filtereddate, CampusCode: logincampusCode },
        });

        const attendanceArr = attendanceResponse.data || [];
        const missingArr = missingAttendanceResponse.data || [];

        // Chart data
        const labels = attendanceArr.map((item) => `${item.ClassName}`);
        const presentCounts = attendanceArr.map((item) => item.TotalPresent);
        const absentCounts = attendanceArr.map((item) => item.TotalAbsent);

        setAttendanceData({
          labels,
          datasets: [
            {
              label: "Present",
              data: presentCounts,
              backgroundColor: "rgba(25,118,210,0.40)",
            },
            {
              label: "Absent",
              data: absentCounts,
              backgroundColor: "rgba(240,120,40,0.55)",
            },
          ],
        });

        // Table data
        const analyticsSummary = missingArr.map((item) => ({
          AttendanceDate: convertToISTDate(item.AttendanceDate),
          TotalClasses: item.TotalClasses,
          TotalClassesSubmittedAttendance: item.TotalClassesSubmittedAttendance,
          TotalClassesMissedAttendance: item.TotalClassesMissedAttendance,
          MissingAttendanceDetails: item.MissingAttendanceDetails || "No missing details",
        }));

        setAnalyticsData(analyticsSummary);
      } catch (err) {
        console.error("Error fetching analytics data:", err);
        setError("Failed to fetch attendance or analytics data.");
        if (err?.response?.status === 401) navigate("/");
      } finally {
        setLoadingAnalytics(false);
      }
    };

    fetchAnalyticsData();
  }, [filters, logincampusCode, authHeader, navigate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Attendance Analytics
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter by class, teacher, and date to view attendance & missing submissions
            </Typography>
          </Box>

          {(loadingLookups || loadingAnalytics) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} sx={{ color: "white" }} />
              <Typography sx={{ fontSize: "0.85rem", opacity: 0.9 }}>Loading…</Typography>
            </Box>
          )}
        </Box>

        {/* ✅ Body */}
        <Box sx={bodySx}>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(25,118,210,0.18)",
              }}
            >
              {error}
            </Alert>
          )}

          {/* ✅ Filters (left stripe section) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" disabled={loadingLookups} sx={selectSx}>
                    <InputLabel>Class</InputLabel>
                    <Select
                      name="class"
                      label="Class"
                      value={filters.class}
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="">All Classes</MenuItem>
                      {classes.map((cls) => (
                        <MenuItem key={cls.ClassID} value={cls.ClassID}>
                          {cls.ClassName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" disabled={loadingLookups} sx={selectSx}>
                    <InputLabel>Teacher</InputLabel>
                    <Select
                      name="teacher"
                      label="Teacher"
                      value={filters.teacher}
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="">All Teachers</MenuItem>
                      {teachers.map((t) => (
                        <MenuItem key={t.TeacherID} value={t.TeacherName}>
                          {t.TeacherName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    type="date"
                    label="Date"
                    name="date"
                    value={filters.date}
                    onChange={handleFilterChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>

              {(loadingLookups || loadingAnalytics) && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      backgroundColor: "rgba(25,118,210,0.10)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: THEME_ORANGE,
                        borderRadius: 999,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Paper>

          {/* ✅ Chart surface */}
          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Attendance Chart
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Box sx={{ height: 420 }}>
              <Bar
                data={attendanceData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: { display: false },
                    tooltip: { mode: "index", intersect: false },
                    legend: { position: "top" },
                  },
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true, ticks: { stepSize: 1, precision: 0 } },
                  },
                }}
              />
            </Box>
          </Paper>

          {/* ✅ Missing Attendance Table surface */}
          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Missing Attendance
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            {/* OUTER – keeps rounded corners & spacing */}
            <Paper elevation={0} sx={tableWrapSx}>

              {/* INNER – ONLY for horizontal scroll */}
              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
              <TableContainer component="div">
              <Table size="small">
                <TableHead>
                  <TableRow sx={theadRowSx}>
                    <TableCell>Attendance Date</TableCell>
                    <TableCell>Total Classes</TableCell>
                    <TableCell>Total Submitted</TableCell>
                    <TableCell>Total Missed</TableCell>
                    <TableCell>Missing Attendance Details</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {analyticsData.map((row, idx) => (
                    <TableRow key={idx} sx={zebraRowSx(idx)}>
                      <TableCell sx={{ textAlign: "center" }}>{row.AttendanceDate}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.TotalClasses}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.TotalClassesSubmittedAttendance}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.TotalClassesMissedAttendance}</TableCell>
                      <TableCell sx={{ textAlign: "center", color: "error.main" }}>
                        {row.MissingAttendanceDetails}
                      </TableCell>
                    </TableRow>
                  ))}

                  {!analyticsData.length && !loadingAnalytics && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center", color: "text.secondary", py: 2 }}>
                        No missing attendance data.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
            </Paper>
          </Paper>

          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={() => setToast((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.type === "error" ? "error" : toast.type === "info" ? "info" : "success"}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(25,118,210,0.18)",
              }}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
            >
              {toast.msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
};

export default AnalyticsAttendance;
